/** @jsxImportSource @emotion/react */
import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAtom, useSetAtom } from 'jotai'
import {
  Box,
  Button,
  Tabs,
  Tab,
  IconButton,
  useTheme,
  Divider,
  Tooltip,
  CircularProgress,
} from '@mui/material'
import { Save, Add, Download, Upload } from '@mui/icons-material'
import { Story, Chapter } from '@app/components/Risk/types'
import {
  builderContentStyles,
  builderSidebarStyles,
  builderPreviewStyles,
  tabPanelStyles,
} from './StoryBuilder.styles'
import { StoryMetadataForm } from './StoryMetadataForm'
import { ChapterEditor } from './ChapterEditor'
import { StoryPreview } from './StoryPreview'
import {
  createEmptyStory,
  createEmptyChapter,
  CreateLocalStoryInput,
} from './types/localStory.types'
import { toast } from '@app/components/ui/Toast/toast'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

const TabPanel = ({ children, value, index }: TabPanelProps) => {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      css={tabPanelStyles}
      sx={{ display: value === index ? 'flex' : 'none', flexDirection: 'column', overflow: 'auto' }}
    >
      {value === index && children}
    </Box>
  )
}

export const StoryBuilderContent = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { storyId } = useParams<{ storyId?: string }>()

  const isEditMode = storyId !== undefined
  const [story, setStory] = useState<Partial<Story>>(createEmptyStory())
  const [isSaving, setIsSaving] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [selectedChapterIndex, setSelectedChapterIndex] = useState(0)

  const handleUpdateStory = useCallback((updates: Partial<Story>) => {
    setStory((prev) => ({ ...prev, ...updates }))
  }, [])

  const handleUpdateChapter = useCallback((chapterIndex: number, updates: Partial<Chapter>) => {
    setStory((prev) => {
      const chapters = [...(prev.chapters || [])]
      chapters[chapterIndex] = { ...chapters[chapterIndex], ...updates }
      return { ...prev, chapters }
    })
  }, [])

  const handleAddChapter = useCallback(() => {
    setStory((prev) => {
      const newChapters = [...(prev.chapters || []), createEmptyChapter()]
      setSelectedChapterIndex(newChapters.length - 1)
      return { ...prev, chapters: newChapters }
    })
  }, [])

  const handleDeleteChapter = useCallback(
    (chapterIndex: number) => {
      setStory((prev) => {
        if (!prev.chapters || prev.chapters.length <= 1) return prev
        const newChapters = prev.chapters.filter((_, i) => i !== chapterIndex)
        if (selectedChapterIndex >= newChapters.length) {
          setSelectedChapterIndex(newChapters.length - 1)
        }
        return { ...prev, chapters: newChapters }
      })
    },
    [selectedChapterIndex],
  )

  const handleMoveChapter = useCallback((chapterIndex: number, direction: 'up' | 'down') => {
    setStory((prev) => {
      if (!prev.chapters) return prev
      const newIndex = direction === 'up' ? chapterIndex - 1 : chapterIndex + 1
      if (newIndex < 0 || newIndex >= prev.chapters.length) return prev

      const newChapters = [...prev.chapters]
      const [movedChapter] = newChapters.splice(chapterIndex, 1)
      newChapters.splice(newIndex, 0, movedChapter)
      setSelectedChapterIndex(newIndex)
      return { ...prev, chapters: newChapters }
    })
  }, [])

  const handleExport = () => {
    if (!story.title) {
      toast({
        title: 'Cannot Export',
        description: 'Please save the story before exporting.',
        type: 'warning',
        position: 'top-right',
      })
      return
    }

    const jsonString = JSON.stringify(story, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${story.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_story.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)

    toast({
      title: 'Story Exported',
      description: 'The story has been downloaded as JSON.',
      type: 'success',
      position: 'top-right',
    })
  }

  const handleImport = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.json'
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (!file) return

      try {
        const text = await file.text()
        const imported = JSON.parse(text)

        if (!imported.title || !imported.chapters || !Array.isArray(imported.chapters)) {
          throw new Error('Invalid story format')
        }

        const {
          id: _id,
          isLocal: _isLocal,
          createdAt: _createdAt,
          updatedAt: _updatedAt,
          ...storyData
        } = imported
        setStory(storyData)

        toast({
          title: 'Story Imported',
          description: "The story has been loaded. Don't forget to save!",
          type: 'success',
          position: 'top-right',
        })
      } catch {
        toast({
          title: 'Import Failed',
          description: 'The file is not a valid story JSON.',
          type: 'error',
          position: 'top-right',
        })
      }
    }
    input.click()
  }

  const chapters = story.chapters || []

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.paper,
        }}
      >
        <Box sx={{ fontWeight: 600 }}>{isEditMode ? 'Edit Story' : 'Create New Story'}</Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Import story from JSON">
            <IconButton size="small" onClick={handleImport}>
              <Upload fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export story to JSON">
            <IconButton size="small" onClick={handleExport}>
              <Download fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Content */}
      <Box css={builderContentStyles}>
        {/* Sidebar */}
        <Box css={builderSidebarStyles(theme)} sx={{ width: 400, minWidth: 400 }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant="fullWidth"
          >
            <Tab label="Details" />
            <Tab label="Chapters" />
          </Tabs>

          <TabPanel value={activeTab} index={0}>
            <StoryMetadataForm story={story} onUpdate={handleUpdateStory} />
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <Box sx={{ p: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Box sx={{ fontWeight: 600 }}>Chapters ({chapters.length})</Box>
                <Button size="small" startIcon={<Add />} onClick={handleAddChapter}>
                  Add Chapter
                </Button>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                {chapters.map((chapter, index) => (
                  <Button
                    key={index}
                    variant={selectedChapterIndex === index ? 'contained' : 'outlined'}
                    onClick={() => setSelectedChapterIndex(index)}
                    sx={{
                      justifyContent: 'flex-start',
                      textTransform: 'none',
                      py: 1,
                    }}
                    size="small"
                  >
                    <Box sx={{ textAlign: 'left' }}>
                      <Box sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                        Ch. {index + 1}
                        {chapter.title &&
                          `: ${chapter.title.substring(0, 15)}${chapter.title.length > 15 ? '...' : ''}`}
                      </Box>
                      <Box sx={{ fontSize: '0.7rem', opacity: 0.7 }}>
                        {chapter.steps.length} step{chapter.steps.length !== 1 ? 's' : ''}
                      </Box>
                    </Box>
                  </Button>
                ))}
              </Box>
            </Box>

            <Divider />

            {chapters[selectedChapterIndex] && (
              <Box sx={{ overflow: 'auto', flex: 1 }}>
                <ChapterEditor
                  chapter={chapters[selectedChapterIndex]}
                  chapterIndex={selectedChapterIndex}
                  onUpdate={(updates) => handleUpdateChapter(selectedChapterIndex, updates)}
                  onDelete={() => handleDeleteChapter(selectedChapterIndex)}
                  onMoveUp={() => handleMoveChapter(selectedChapterIndex, 'up')}
                  onMoveDown={() => handleMoveChapter(selectedChapterIndex, 'down')}
                  canDelete={chapters.length > 1}
                  canMoveUp={selectedChapterIndex > 0}
                  canMoveDown={selectedChapterIndex < chapters.length - 1}
                />
              </Box>
            )}
          </TabPanel>
        </Box>

        {/* Preview */}
        <Box css={builderPreviewStyles}>
          <StoryPreview
            story={story}
            selectedChapterIndex={activeTab === 1 ? selectedChapterIndex : undefined}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default StoryBuilderContent
